import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import { link, paragraphSmall } from '@helpers/typography';
import { ContentType } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
import NavigationStyles from '../Navigation.styles';
const dropdownBaseStyles = css `
  ${transition('opacity, transform', 0.5)};

  display: flex;
  flex-direction: column;
  gap: ${fluid(22, 24)};
  position: absolute;
  top: 0;
  z-index: 1;
  left: 0;
  width: 100%;
  padding: var(--dropdownPadTop) var(--dropdownPadX) var(--dropdownPadBottom);
  background-color: ${brand.global.white};
  max-height: calc(100dvh - var(--headerHeight) - var(--tabsHeight) - 16px);
  border-radius: var(--dropdownRadius);
  overflow: auto;
  pointer-events: auto;
  overscroll-behavior: contain;

  @media ${from(Device.TabletLarge)} {
    z-index: -2; // Ensure the dropdown slides out from beneath the main body of the header
    max-height: calc(100dvh - var(--headerHeight) - 32px);
  }
`;
const dropdownHiddenStyles = css `
  opacity: 0;
  pointer-events: none;
  transform: translateX(33%);

  @media ${from(Device.TabletLarge)} {
    transform: translateY(-33%);
  }
`;
const Container = styled.nav `
  --dropdownPadTop: ${fluid(12, 22)};
  --dropdownPadX: var(--sitePadding);
  --dropdownPadBottom: ${fluid(20, 34)};
  --dropdownRadius: 0px 0px ${fluid(20, 40)} 0px;

  color: ${brand.blue.navy};
  background-color: ${brand.global.white};
`;
const Links = styled.ul `
  display: flex;
  flex-direction: column;
  gap: ${fluid(22, 40)};

  @media ${until(Device.TabletLarge)} {
    ${dropdownBaseStyles};
    ${transition('opacity, transform, background-color', 0.5)};

    top: calc(var(--headerHeight) + var(--tabsHeight));
    z-index: -2; // Ensure the dropdown slides out from beneath the main body of the header
    overflow: initial;

    ${NavigationStyles.Container}[data-menu-open='false'] & {
      ${dropdownHiddenStyles};

      transform: translateY(-33%);
    }

    li {
      width: 100%;
    }

    &:has(${() => TopLevelLink}[aria-expanded='true']) {
      background-color: transparent;
      pointer-events: none;

      ${() => TopLevelLink} {
        opacity: 0;
      }
    }
  }

  @media ${from(Device.TabletLarge)} {
    flex-direction: row;
    justify-content: center;
    height: 100%;

    li {
      display: inline-flex;
      height: 100%;

      &:has(${() => Dropdown}[data-variant='simple']) {
        position: relative;
      }
    }
  }
`;
const TopLevelLink = styled.a `
  ${ButtonReset};
  ${fonts.proximaNova.bold};

  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  color: currentColor;
  font-size: 18px;
  line-height: 26px;
  text-decoration: none;
  text-align: left;

  svg {
    display: block;
    flex-shrink: 0;
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform, opacity', 0.15)};

    width: 100%;

    &:active {
      transform: translateY(1.5px);
    }

    svg {
      transform: rotate(-90deg);
      width: 13px;
    }
  }

  @media ${from(Device.TabletLarge)} {
    position: relative;
    height: 100%;
    font-size: ${fluid(14, 18)};

    svg {
      ${transition('transform')};

      width: ${fluid(8, 10)};
      color: ${brand.green.default};
    }

    &::after {
      ${transition('transform')};

      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      height: 5px;
      width: 100%;
      background-color: currentColor;
      transform: scaleX(0);
    }

    &:focus-visible,
    &:hover,
    &[aria-expanded='true'] {
      &::after {
        transform: scaleX(1);
      }
    }

    &[aria-expanded='true'] svg {
      transform: rotate(180deg);
    }
  }
`;
const Dropdown = styled.div `
  ${dropdownBaseStyles};

  li:has(${TopLevelLink}[aria-expanded='false']) & {
    ${dropdownHiddenStyles};
  }

  > ul,
  > ul > li {
    display: contents;

    > a,
    > button {
      ${transition('opacity', 0.5)};
    }
  }

  @media ${until(Device.TabletLarge)} {
    overflow: hidden;

    > ul {
      display: flex;
      flex-direction: column;
      gap: inherit;
      overflow: auto;
      overscroll-behavior: contain;
      padding-bottom: 1px; // Prevents a slight scroll on the dropdown
    }

    &:has(${() => ColumnLinks}[data-active='true']) {
      background-color: transparent;
      pointer-events: none;
      overflow: visible;

      > ul > li > a,
      > ul > li > button,
      ${() => CardWrapper} {
        opacity: 0;
      }
    }
  }

  @media ${from(Device.TabletLarge)} {
    --dropdownPadX: ${fluid(16, 20)};

    top: 100%;
    box-shadow: 0px 14px 16px 2px ${rgba(brand.global.black, 0.1)};
    gap: 18px;

    &[data-variant='simple'] {
      white-space: nowrap;
      width: auto;
      min-width: 362px;
      padding-right: calc(var(--dropdownPadX) * 2);
    }

    &[data-variant='complex'] {
      --dropdownPadBottom: ${fluid(34, 84)};

      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr 2fr;
      grid-template-rows: min-content min-content min-content;
      gap: 26px 18px;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    ${scrollbars};
  }

  /* Dropdown colour definitions */
  --iconBackground: ${brand.green.default};
  --iconColour: ${brand.global.white};
  --cardWrapperBackground: ${brand.green.light};
  --cardBackground: ${brand.green.gradient};
  --cardIconColour: ${brand.green.default};

  &[data-type=${ContentType.Intermediaries}] {
    --iconBackground: ${brand.green.light};
    --iconColour: ${brand.blue.navy};
    --cardBackground: ${brand.blue.navy};
  }

  &[data-type=${ContentType.Savings}] {
    --direction: 0deg;
    --iconBackground: ${brand.blue.default};
    --cardWrapperBackground: ${brand.blue.light};
    --cardBackground: ${brand.blue.gradient};
    --cardIconColour: ${brand.blue.default};
  }
`;
const PrimaryPageLink = styled.a `
  ${fonts.proximaNova.bold};

  font-size: ${fluid(18, 30)};
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
  gap: ${fluid(14, 22)};
  color: currentColor;
  text-decoration: none;
  margin-bottom: -4px;

  span {
    ${transition('transform')};

    display: grid;
    place-content: center;
    width: ${fluid(20, 28)};
    height: ${fluid(20, 28)};
    background-color: var(--iconBackground);
    border-radius: 5000px;
  }

  svg {
    display: block;
    width: ${fluid(13, 18)};
    color: var(--iconColour);
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }

  @media ${from(Device.TabletLarge)} {
    margin-bottom: ${fluid(4, 8)};
    grid-column: 1 / span 4;

    ${Dropdown}[data-variant='complex'] & {
      margin-bottom: 0;
    }

    &:focus-visible,
    &:hover {
      span {
        transform: translateX(4px);
      }
    }
  }
`;
const ColumnLinks = styled.ul `
  display: flex;
  flex-direction: column;

  @media ${until(Device.TabletLarge)} {
    ${dropdownBaseStyles};

    z-index: 2;

    &[data-active='false'] {
      ${dropdownHiddenStyles};
    }
  }

  @media ${from(Device.TabletLarge)} {
    gap: 13px;
    margin-bottom: auto;
  }
`;
const CardWrapper = styled.ul `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${fluid(10, 24)};

  > li {
    display: contents;
  }

  @media ${from(Device.TabletLarge)} {
    background-color: var(--cardWrapperBackground);
    padding: ${fluid(26, 32)} ${fluid(18, 26)};
    margin: calc(var(--dropdownPadTop) * -1) calc(var(--dropdownPadX) * -1)
      calc(var(--dropdownPadBottom) * -1) 0;
    grid-column: 5 / span 1;
    grid-row: 1 / -1;
    border-radius: var(--dropdownRadius);
    place-content: center;
  }
`;
const ColumnLink = styled.a `
  ${ButtonReset};
  ${fonts.proximaNova.regular};
  ${link};

  font-size: 16px;
  line-height: 24px;
  color: currentColor;
  text-decoration-color: transparent;
  text-align: left;

  button& {
    ${fonts.proximaNova.bold};

    display: inline-flex;
    gap: 14px;
    align-items: center;
    justify-content: space-between;

    svg {
      display: block;
      transform: rotate(-90deg);
      width: 13px;
    }
  }

  &[data-heading='true'],
  ${Dropdown}[data-variant='simple'] & {
    ${fonts.proximaNova.bold};

    font-size: 18px;
  }

  a& {
    &:focus-visible,
    &:hover {
      text-decoration-color: currentColor;
    }

    /* Mobile click styles - button should mimic press */
    @media ${until(Device.TabletLarge)} {
      ${transition('transform', 0.15)};

      &:active {
        transform: translateY(1.5px);
      }
    }
  }

  @media ${from(Device.TabletLarge)} {
    button& {
      display: none;
    }

    &[data-heading='true'] {
      margin-bottom: 7px;
    }
  }
`;
const Card = styled.a `
  ${transition('transform, opacity')};

  ${paragraphSmall};
  ${fonts.proximaNova.bold};

  --borderRadius: ${fluid(10, 20)};

  display: flex;
  align-items: center;
  flex-direction: column;
  gap: ${fluid(4, 10)};
  padding: ${fluid(12, 18)} 18px;
  background: var(--cardBackground);
  border-radius: var(--borderRadius) var(--borderRadius) var(--borderRadius) 0px;
  text-decoration: none;
  text-align: center;
  color: ${brand.global.white};
  margin: 0;

  span {
    display: grid;
    place-content: center;
    border-radius: 5000px;
    width: ${fluid(26, 70)};
    height: ${fluid(26, 70)};
    background-color: ${brand.global.beige};
  }

  svg {
    display: block;
    width: ${fluid(20, 40)};
    color: var(--cardIconColour);
  }

  &:focus-visible {
    outline: auto;
    outline-offset: 2px;
    outline-width: 1px;
    outline-style: solid;
    outline-color: var(--iconBackground);
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    &:active {
      transform: translateY(1.5px);
    }
  }

  @media ${from(Device.TabletLarge)} {
    &:focus-visible,
    &:hover {
      opacity: 0.8;
      transform: translateY(-4px);
    }
  }
`;
const BackButton = styled.button `
  ${ButtonReset};
  ${fonts.proximaNova.semiBold};

  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: currentColor;
  text-align: left;
  font-size: 12px;
  line-height: 24px;
  margin-bottom: -14px;

  &:active {
    transform: translateY(1.5px);
  }

  svg {
    transform: rotate(90deg);
    width: 13px;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const NavigationLinksStyles = {
    Container,
    Links,
    TopLevelLink,
    Dropdown,
    CardWrapper,
    PrimaryPageLink,
    Card,
    ColumnLinks,
    ColumnLink,
    BackButton,
};
export default NavigationLinksStyles;
